import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../App';
import { 
    Box, Typography, Button, TextField, Paper, Grid, 
    Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Stack, Chip, Container, Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { FaPlus, FaTrash, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ConfirmModal';

const AdminTournaments = () => {
    const [tournaments, setTournaments] = useState([]);
    const [archivedTournaments, setArchivedTournaments] = useState([]);
    const [open, setOpen] = useState(false);
    
    // Form State
    const [name, setName] = useState('');
    const [date, setDate] = useState(dayjs());
    const [startTime, setStartTime] = useState(null); // Time only
    const [deadline, setDeadline] = useState(dayjs().subtract(1, 'day')); 
    const [description, setDescription] = useState('');
    const [contact, setContact] = useState('Vorstand');
    
    // Categories
    const [categories, setCategories] = useState([
        { name: 'Damen', maxPlayers: 12 },
        { name: 'Herren', maxPlayers: 12 }
    ]);
    
    // Food Options
    const [foodOptions, setFoodOptions] = useState(['Kuchen', 'Salat']);
    const [newFoodOption, setNewFoodOption] = useState('');

    // Delete State
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [tournamentToDelete, setTournamentToDelete] = useState(null);
    
    // Archive State
    const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
    const [tournamentToArchive, setTournamentToArchive] = useState(null);
    
    // Expandable Row State
    const [expandedRow, setExpandedRow] = useState(null);

    const fetchTournaments = () => {
        axios.get(`${API_BASE_URL}/api/admin/tournaments`, { withCredentials: true })
            .then(res => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                // Separate into active (future) and archived (past or manually archived)
                const active = res.data.filter(t => {
                    const tournamentDate = new Date(t.date);
                    tournamentDate.setHours(0, 0, 0, 0);
                    return tournamentDate >= today && !t.archived;
                });
                
                const archived = res.data.filter(t => {
                    const tournamentDate = new Date(t.date);
                    tournamentDate.setHours(0, 0, 0, 0);
                    return tournamentDate < today || t.archived;
                });
                
                setTournaments(active);
                setArchivedTournaments(archived);
            })
            .catch(err => console.error(err));
    };

    useEffect(() => {
        fetchTournaments();
    }, []);

    const handleCreate = () => {
        // Combine date with startTime if provided
        let startDate = null;
        if (startTime) {
            startDate = date.clone()
                .hour(startTime.hour())
                .minute(startTime.minute())
                .toDate();
        }

        const data = {
            name,
            date: date.toDate(),
            startDate,
            deadline: deadline ? deadline.toDate() : null,
            description,
            contact,
            categories,
            foodOptions
        };

        axios.post(`${API_BASE_URL}/api/tournaments`, data, { withCredentials: true })
            .then(res => {
                toast.success("Turnier angelegt!");
                setOpen(false);
                fetchTournaments();
                // Reset form
                setName('');
                setDescription('');
                setStartTime(null);
                setDeadline(dayjs().subtract(1, 'day'));
                setCategories([
                    { name: 'Damen', maxPlayers: 12 },
                    { name: 'Herren', maxPlayers: 12 }
                ]);
                setFoodOptions(['Kuchen', 'Salat']);
            })
            .catch(err => {
                toast.error("Fehler: " + (err.response?.data?.message || err.message));
            });
    };

    const addFoodOption = () => {
        if (newFoodOption.trim()) {
            setFoodOptions([...foodOptions, newFoodOption.trim()]);
            setNewFoodOption('');
        }
    };

    const removeFoodOption = (index) => {
        setFoodOptions(foodOptions.filter((_, i) => i !== index));
    };

    const handleDeleteClick = (id) => {
        setTournamentToDelete(id);
        setConfirmDeleteOpen(true);
    };

    const confirmDelete = () => {
        if (!tournamentToDelete) return;
        
        axios.delete(`${API_BASE_URL}/api/tournaments/${tournamentToDelete}`, { withCredentials: true })
            .then(() => {
                toast.success("Turnier gelöscht");
                fetchTournaments();
                setConfirmDeleteOpen(false);
                setTournamentToDelete(null);
            })
            .catch(err => {
                 toast.error("Fehler beim Löschen: " + (err.response?.data?.message || err.message));
            });
    };

    const handleArchiveClick = (id) => {
        setTournamentToArchive(id);
        setConfirmArchiveOpen(true);
    };

    const confirmArchive = () => {
        if (!tournamentToArchive) return;
        
        axios.post(`${API_BASE_URL}/api/tournaments/${tournamentToArchive}/archive`, {}, { withCredentials: true })
            .then(() => {
                toast.success("Turnier archiviert");
                fetchTournaments();
                setConfirmArchiveOpen(false);
                setTournamentToArchive(null);
            })
            .catch(err => {
                 toast.error("Fehler beim Archivieren: " + (err.response?.data?.message || err.message));
            });
    };

    const handleCategoryChange = (index, field, value) => {
        const newCats = [...categories];
        newCats[index][field] = value;
        setCategories(newCats);
    };

    const addCategory = () => {
        setCategories([...categories, { name: '', maxPlayers: 12 }]);
    };

    const removeCategory = (index) => {
        setCategories(categories.filter((_, i) => i !== index));
    };

    return (
        <Container maxWidth="lg" disableGutters>
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 2,
                    mb: 3
                }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
                        Turnierverwaltung
                    </Typography>
                    <Button variant="contained" startIcon={<FaPlus />} onClick={() => setOpen(true)}>
                        Neues Turnier
                    </Button>
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: 50 }}></TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Kategorien</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Teilnehmer</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tournaments.map(t => (
                                <React.Fragment key={t._id}>
                                    <TableRow hover>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => setExpandedRow(expandedRow === t._id ? null : t._id)}>
                                                {expandedRow === t._id ? <FaChevronUp /> : <FaChevronDown />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{t.name}</TableCell>
                                        <TableCell>{dayjs(t.date).format('DD.MM.YYYY')}</TableCell>
                                        <TableCell>
                                            {t.categories.map(c => (
                                                <Chip key={c.name} label={`${c.name} (${c.maxPlayers})`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                            ))}
                                        </TableCell>
                                        <TableCell>{t.participants.length}</TableCell>
                                        <TableCell>
                                            {(() => {
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                const tournamentDate = new Date(t.date);
                                                tournamentDate.setHours(0, 0, 0, 0);
                                                const isPast = tournamentDate < today;
                                                
                                                return isPast ? (
                                                    <IconButton color="warning" onClick={() => handleArchiveClick(t._id)} size="small" title="Archivieren">
                                                        <FaTrash />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton color="error" onClick={() => handleDeleteClick(t._id)} size="small" title="Löschen">
                                                        <FaTrash />
                                                    </IconButton>
                                                );
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                            <Collapse in={expandedRow === t._id} timeout="auto" unmountOnExit>
                                                <Box sx={{ margin: 2 }}>
                                                    <Typography variant="h6" gutterBottom component="div">
                                                        Teilnehmer
                                                    </Typography>
                                                    {t.participants.length === 0 ? (
                                                        <Typography variant="body2" color="text.secondary">Keine Anmeldungen</Typography>
                                                    ) : (
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Name</TableCell>
                                                                    <TableCell>Kategorie</TableCell>
                                                                    <TableCell>Status</TableCell>
                                                                    <TableCell>Mitbringsel</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {t.participants.map((p, idx) => (
                                                                    <TableRow key={idx}>
                                                                        <TableCell>{p.name} {p.isGuest && '(Gast)'}</TableCell>
                                                                        <TableCell>{p.category}</TableCell>
                                                                        <TableCell>
                                                                            <Chip 
                                                                                label={p.status} 
                                                                                size="small" 
                                                                                color={p.status === 'Active' ? 'success' : 'default'}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>{p.food || '-'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {archivedTournaments.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" component="h2" fontWeight="bold" color="text.secondary" sx={{ mb: 2 }}>
                            Archivierte Turniere ({archivedTournaments.length})
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Teilnehmer</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Aktionen</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {archivedTournaments.map(t => (
                                        <TableRow key={t._id} hover>
                                            <TableCell>{t.name}</TableCell>
                                            <TableCell>{dayjs(t.date).format('DD.MM.YYYY')}</TableCell>
                                            <TableCell>
                                                {t.archived ? (
                                                    <Chip label={`Archiviert am ${dayjs(t.archivedAt).format('DD.MM.YYYY')}`} size="small" color="default" />
                                                ) : (
                                                    <Chip label="Vergangen" size="small" color="warning" />
                                                )}
                                            </TableCell>
                                            <TableCell>{t.participants.length}</TableCell>
                                            <TableCell>
                                                {!t.archived && (
                                                    <IconButton color="warning" onClick={() => handleArchiveClick(t._id)} size="small" title="Archivieren">
                                                        <FaTrash />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Paper>

            <ConfirmModal 
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={confirmDelete}
                title="Turnier löschen"
                message="Möchten Sie dieses Turnier wirklich unwiderruflich löschen?"
                confirmText="Löschen"
                type="delete"
            />

            <ConfirmModal 
                open={confirmArchiveOpen}
                onClose={() => setConfirmArchiveOpen(false)}
                onConfirm={confirmArchive}
                title="Turnier archivieren"
                message="Möchten Sie dieses Turnier archivieren? Es wird nach 6 Monaten automatisch gelöscht."
                confirmText="Archivieren"
                type="warning"
            />

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Turnier anlegen</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField 
                            label="Name des Turniers" 
                            fullWidth 
                            value={name} 
                            onChange={(e) => setName(e.target.value)} 
                        />
                        <DatePicker 
                            label="Datum"
                            value={date}
                            onChange={(newValue) => setDate(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                            format="DD.MM.YYYY"
                        />
                        <TimePicker 
                            label="Startzeit (optional)"
                            value={startTime}
                            onChange={(newValue) => setStartTime(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                            ampm={false}
                        />
                        <DatePicker 
                            label="Anmeldeschluss"
                            value={deadline}
                            onChange={(newValue) => setDeadline(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                            format="DD.MM.YYYY"
                        />
                        <TextField 
                            label="Beschreibung" 
                            fullWidth 
                            multiline 
                            rows={3}
                            value={description} 
                            onChange={(e) => setDescription(e.target.value)} 
                        />
                        <TextField 
                            label="Ansprechpartner" 
                            fullWidth 
                            value={contact} 
                            onChange={(e) => setContact(e.target.value)} 
                        />

                        <Typography variant="h6">Kategorien</Typography>
                        {categories.map((cat, index) => (
                            <Box key={index} display="flex" gap={1} alignItems="center">
                                <TextField 
                                    label="Name" 
                                    size="small"
                                    value={cat.name}
                                    onChange={(e) => handleCategoryChange(index, 'name', e.target.value)}
                                />
                                <TextField 
                                    label="Max. Spieler" 
                                    type="number"
                                    size="small"
                                    sx={{ width: 100 }}
                                    value={cat.maxPlayers}
                                    onChange={(e) => handleCategoryChange(index, 'maxPlayers', e.target.value)}
                                />
                                <IconButton color="error" onClick={() => removeCategory(index)}>
                                    <FaTrash size={16} />
                                </IconButton>
                            </Box>
                        ))}
                        <Button startIcon={<FaPlus />} onClick={addCategory} sx={{ alignSelf: 'flex-start' }}>
                            Kategorie hinzufügen
                        </Button>

                        <Typography variant="h6" sx={{ mt: 2 }}>Optionen (Mitbringsel)</Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {foodOptions.map((opt, index) => (
                                <Chip 
                                    key={index} 
                                    label={opt} 
                                    onDelete={() => removeFoodOption(index)} 
                                    color="secondary" 
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                        <Box display="flex" gap={1}>
                             <TextField 
                                label="Neue Option" 
                                size="small"
                                value={newFoodOption}
                                onChange={(e) => setNewFoodOption(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && addFoodOption()}
                            />
                            <Button variant="outlined" onClick={addFoodOption}>Hinzufügen</Button>
                        </Box>

                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={!name}>
                        Anlegen
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminTournaments;
